import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Menu, MenuItem, Skeleton, Tooltip } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import DirectionsRunRoundedIcon from "@mui/icons-material/DirectionsRunRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import DonutLargeRoundedIcon from "@mui/icons-material/DonutLargeRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LibraryAddCheckRoundedIcon from "@mui/icons-material/LibraryAddCheckRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import API_BASE_URL from "../../components/config/appConfig";
import { CountUp, DeliveryTrendChart, MixDonut, SprintProgressChart } from "./charts";
import { ProjectList, ProjectTile } from "./ProjectCards";
import ProjectDetail from "./ProjectDetail";
import { HEALTH, HEALTH_HELP, SORTS, buildPortfolio, buildProjectView, filterViews, sortViews, toSegments } from "./reportModel";
import "./style.scss";

const VIEW_KEY = "projam.reports.view";
const MIX_KINDS = [
  { key: "status", label: "Status", field: "byStatus" },
  { key: "type", label: "Type", field: "byType" },
  { key: "priority", label: "Priority", field: "byPriority" },
];

const readView = () => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === "list" ? "list" : "tile";
  } catch (error) {
    return "tile";
  }
};

const KpiTile = ({ icon, tone, label, value, suffix = "", display, caption, index }) => (
  <div className={`pr-kpi pr-tone--${tone}`} style={{ "--d": `${index * 50}ms` }} aria-label={`${label}: ${display ?? `${value}${suffix}`}`}>
    <span className="pr-kpi__icon">{icon}</span>
    <span className="pr-kpi__label">{label}</span>
    <strong className="pr-kpi__value">{display ?? <CountUp value={value} suffix={suffix} />}</strong>
    {caption ? <span className="pr-kpi__caption">{caption}</span> : null}
  </div>
);

const ChartCard = ({ icon, title, subtitle, action, children, className = "", index = 0 }) => (
  <section className={`pr-card ${className}`} style={{ "--d": `${120 + index * 60}ms` }}>
    <header className="pr-card__head">
      <span className="pr-card__icon">{icon}</span>
      <div className="pr-card__title">
        <h2>{title}</h2>
        {subtitle ? <span>{subtitle}</span> : null}
      </div>
      {action ? <div className="pr-card__action">{action}</div> : null}
    </header>
    {children}
  </section>
);

const ViewReports = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /* The selection arrives in navigation state from the projects page; it is
     mirrored into ?projects= so a refresh or a shared link shows the same report. */
  const stateIds = location.state?.projectIds;
  const projectIds = useMemo(() => {
    if (Array.isArray(stateIds) && stateIds.length) return stateIds.map(String);
    const fromQuery = searchParams.get("projects");
    return fromQuery ? fromQuery.split(",").filter(Boolean) : [];
  }, [stateIds, searchParams]);
  const idsKey = projectIds.join(",");

  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState(projectIds.length ? "loading" : "empty");
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [view, setView] = useState(readView);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("health");
  const [sortAnchor, setSortAnchor] = useState(null);
  const [mixKind, setMixKind] = useState("status");
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    if (Array.isArray(stateIds) && stateIds.length && searchParams.get("projects") !== idsKey) {
      setSearchParams({ projects: idsKey }, { replace: true, state: location.state });
    }
    // only when the selection itself changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!projectIds.length) {
        setStatus("empty");
        return;
      }
      if (quiet) setRefreshing(true);
      else setStatus("loading");
      try {
        const { data } = await axios.post(`${API_BASE_URL}/projects/reports`, { projectIds });
        setReports(Array.isArray(data) ? data.filter(Boolean) : []);
        setUpdatedAt(new Date());
        setStatus("ready");
      } catch (error) {
        console.error("Could not load the projects report", error);
        setStatus("error");
      } finally {
        setRefreshing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey]
  );

  useEffect(() => {
    load();
  }, [load]);

  const changeView = (next) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch (error) {
      // a private window can refuse storage; the toggle still works for this visit
    }
  };

  const views = useMemo(() => reports.map((report, index) => buildProjectView(report, index)), [reports]);
  const portfolio = useMemo(() => buildPortfolio(views), [views]);
  const visible = useMemo(() => sortViews(filterViews(views, query), sortKey), [views, query, sortKey]);
  const detail = views.find((each) => each.id === detailId) || null;
  const mix = MIX_KINDS.find((kind) => kind.key === mixKind) || MIX_KINDS[0];

  const healthBar = [
    { key: "onTrack", count: portfolio.healthCounts.onTrack },
    { key: "atRisk", count: portfolio.healthCounts.atRisk },
    { key: "offTrack", count: portfolio.healthCounts.offTrack },
    { key: "idle", count: portfolio.healthCounts.idle },
  ];

  const isReady = status === "ready";

  return (
    <div className="pr-page">
      {/* ------------------------------------------------------------ header */}
      <section className="pr-hero">
        <div className="pr-hero__top">
          <button type="button" className="pr-back" onClick={() => navigate("/projects")}>
            <ArrowBackRoundedIcon />
            Projects
          </button>
          <div className="pr-hero__actions">
            {updatedAt ? (
              <span className="pr-updated">
                <ScheduleRoundedIcon />
                Updated {updatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            ) : null}
            <Tooltip title="Refresh report">
              <span>
                <button
                  type="button"
                  className={`pr-icon-btn ${refreshing ? "is-spinning" : ""}`}
                  onClick={() => load({ quiet: true })}
                  disabled={!projectIds.length || status === "loading" || refreshing}
                  aria-label="Refresh report"
                >
                  <RefreshRoundedIcon />
                </button>
              </span>
            </Tooltip>
          </div>
        </div>

        <div className="pr-hero__main">
          <span className="pr-hero__tile" aria-hidden="true">
            <InsightsRoundedIcon />
          </span>
          <div className="pr-hero__heading">
            <span className="pr-eyebrow">Portfolio</span>
            <h1>Projects report</h1>
            <div className="pr-hero__meta">
              <span className="pr-meta-chip">
                <FolderRoundedIcon />
                {projectIds.length} {projectIds.length === 1 ? "project" : "projects"} selected
              </span>
              {isReady ? (
                <span className="pr-meta-chip">
                  <DirectionsRunRoundedIcon />
                  {portfolio.activeSprints} active {portfolio.activeSprints === 1 ? "sprint" : "sprints"}
                </span>
              ) : null}
            </div>
          </div>

          {isReady && views.length ? (
            <Tooltip title={HEALTH_HELP}>
              <div className="pr-health-summary" aria-label="Project health">
                <div className="pr-health-summary__bar">
                  {healthBar.map((entry) =>
                    entry.count ? (
                      <span key={entry.key} className={`pr-tone--${HEALTH[entry.key].tone}`} style={{ flexGrow: entry.count }} />
                    ) : null
                  )}
                </div>
                <div className="pr-health-summary__legend">
                  {healthBar.map((entry) => (
                    <span key={entry.key} className={`pr-tone--${HEALTH[entry.key].tone}`}>
                      <i />
                      {HEALTH[entry.key].label} <b>{entry.count}</b>
                    </span>
                  ))}
                </div>
              </div>
            </Tooltip>
          ) : null}
        </div>
      </section>

      {/* ------------------------------------------------------------- states */}
      {status === "empty" ? (
        <div className="pr-state">
          <span className="pr-state__icon">
            <LibraryAddCheckRoundedIcon />
          </span>
          <h2>Pick projects to compare</h2>
          <p>Select one or more projects on the Projects page, then choose View reports.</p>
          <Button className="pr-btn pr-btn--primary" startIcon={<FolderRoundedIcon />} onClick={() => navigate("/projects")} disableRipple>
            Go to projects
          </Button>
        </div>
      ) : null}

      {status === "error" ? (
        <div className="pr-state pr-state--error" role="alert">
          <span className="pr-state__icon">
            <ErrorOutlineRoundedIcon />
          </span>
          <h2>Couldn't load the report</h2>
          <p>Check your connection and try again.</p>
          <Button className="pr-btn pr-btn--primary" startIcon={<RefreshRoundedIcon />} onClick={() => load()} disableRipple>
            Retry
          </Button>
        </div>
      ) : null}

      {status === "loading" ? (
        <div className="pr-skeleton" aria-label="Loading report">
          <div className="pr-kpis">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={112} />
            ))}
          </div>
          <div className="pr-charts">
            <Skeleton variant="rounded" height={280} />
            <Skeleton variant="rounded" height={280} />
          </div>
          <div className="pr-tiles">
            {Array.from({ length: Math.min(Math.max(projectIds.length, 2), 6) }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={360} />
            ))}
          </div>
        </div>
      ) : null}

      {isReady ? (
        <>
          {/* -------------------------------------------------------- projects */}
          <section className="pr-projects">
            <header className="pr-projects__head">
              <div className="pr-projects__title">
                <h2>Projects</h2>
                <span className="pr-count">{visible.length}</span>
              </div>
              <div className="pr-projects__tools">
                <label className="pr-search">
                  <SearchRoundedIcon />
                  <input type="search" placeholder="Filter projects" value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Filter projects" />
                </label>
                <button type="button" className="pr-pill" onClick={(event) => setSortAnchor(event.currentTarget)} aria-haspopup="menu" aria-label={`Sort by ${SORTS[sortKey].label}`}>
                  <SortRoundedIcon />
                  {SORTS[sortKey].label}
                </button>
                <Menu
                  anchorEl={sortAnchor}
                  open={Boolean(sortAnchor)}
                  onClose={() => setSortAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{ paper: { className: "pr-menu" } }}
                >
                  {Object.entries(SORTS).map(([key, sort]) => (
                    <MenuItem
                      key={key}
                      selected={key === sortKey}
                      onClick={() => {
                        setSortKey(key);
                        setSortAnchor(null);
                      }}
                    >
                      {sort.label}
                      {key === sortKey ? <CheckRoundedIcon className="pr-menu__check" /> : null}
                    </MenuItem>
                  ))}
                </Menu>
                <div className="pr-seg" role="tablist" aria-label="Layout" data-active={view}>
                  <span className="pr-seg__thumb" aria-hidden="true" />
                  <button type="button" role="tab" aria-selected={view === "tile"} className={view === "tile" ? "is-active" : ""} onClick={() => changeView("tile")}>
                    <GridViewRoundedIcon />
                    Tiles
                  </button>
                  <button type="button" role="tab" aria-selected={view === "list"} className={view === "list" ? "is-active" : ""} onClick={() => changeView("list")}>
                    <ViewListRoundedIcon />
                    List
                  </button>
                </div>
              </div>
            </header>

            {!visible.length ? (
              <div className="pr-state pr-state--inline">
                <span className="pr-state__icon">
                  <SearchOffRoundedIcon />
                </span>
                <h2>{views.length ? "No projects match that filter" : "No projects found"}</h2>
                <p>{views.length ? "Try a different name or key." : "The selected projects may have been removed."}</p>
              </div>
            ) : view === "tile" ? (
              <div className="pr-tiles" key="tile">
                {visible.map((each, index) => (
                  <ProjectTile key={each.id} view={each} index={index} onOpen={(item) => setDetailId(item.id)} />
                ))}
              </div>
            ) : (
              <div className="pr-list-wrap" key="list">
                <ProjectList views={visible} onOpen={(item) => setDetailId(item.id)} />
              </div>
            )}
          </section>

          {/* ------------------------------------------------------------ KPIs */}
          <div className="pr-kpis">
            <KpiTile
              index={0}
              tone="green"
              icon={<TaskAltRoundedIcon />}
              label="Sprint completion"
              value={portfolio.completion}
              suffix="%"
              caption={`${portfolio.done} of ${portfolio.total} sprint issues done`}
            />
            <KpiTile
              index={1}
              tone="blue"
              icon={<PendingActionsRoundedIcon />}
              label="Open issues"
              value={portfolio.open}
              caption={portfolio.unassigned === null ? "across all projects" : `${portfolio.unassigned} unassigned`}
            />
            <KpiTile
              index={2}
              tone="violet"
              icon={<Inventory2RoundedIcon />}
              label="Backlog"
              value={portfolio.backlog ?? 0}
              display={portfolio.backlog === null ? "—" : undefined}
              caption="waiting to be planned"
            />
            <KpiTile
              index={3}
              tone="teal"
              icon={<DirectionsRunRoundedIcon />}
              label="Active sprints"
              value={portfolio.activeSprints}
              display={`${portfolio.activeSprints}/${portfolio.projects}`}
              caption="projects currently sprinting"
            />
            <KpiTile index={4} tone="amber" icon={<GroupsRoundedIcon />} label="People" value={portfolio.people} caption="unique across projects" />
            <KpiTile
              index={5}
              tone="red"
              icon={<BoltRoundedIcon />}
              label="Story points"
              value={portfolio.storyPoints.done}
              display={portfolio.storyPoints.total ? undefined : "—"}
              caption={portfolio.storyPoints.total ? `delivered of ${portfolio.storyPoints.total}` : "no estimates yet"}
            />
          </div>

          {/* ---------------------------------------------------------- charts */}
          <div className="pr-charts">
            <ChartCard
              index={0}
              icon={<BarChartRoundedIcon />}
              title="Sprint progress"
              subtitle="Done and remaining in each project's current sprint"
            >
              <SprintProgressChart views={views} />
            </ChartCard>
            <ChartCard
              index={1}
              icon={<DonutLargeRoundedIcon />}
              title="Work mix"
              subtitle="Every issue across the selected projects"
              action={
                <div className="pr-seg pr-seg--sm" role="tablist" aria-label="Break down by">
                  {MIX_KINDS.map((kind) => (
                    <button
                      key={kind.key}
                      type="button"
                      role="tab"
                      aria-selected={mixKind === kind.key}
                      className={mixKind === kind.key ? "is-active" : ""}
                      onClick={() => setMixKind(kind.key)}
                    >
                      {kind.label}
                    </button>
                  ))}
                </div>
              }
            >
              <MixDonut key={mix.key} segments={toSegments(portfolio[mix.field], mix.key)} />
            </ChartCard>
            <ChartCard
              index={2}
              className="pr-card--wide"
              icon={<ShowChartRoundedIcon />}
              title="Delivery trend"
              subtitle="Completion of each project's recent sprints — tap a project to hide it"
            >
              <DeliveryTrendChart views={views} />
            </ChartCard>
          </div>
        </>
      ) : null}

      <ProjectDetail
        view={detail}
        open={Boolean(detail)}
        onClose={() => setDetailId(null)}
        onOpenBoard={(item) => navigate(`/projects/${item.slug}/active-sprints`)}
      />
    </div>
  );
};

export default ViewReports;
